import { Injectable, signal } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { GenesysCloudService } from '../genesys-cloud.service';

/** Keeps an open Lead UI popup on the deployed build without interrupting a lead. */
@Injectable({ providedIn: 'root' })
export class LeadUiUpdateService {
  private readonly checkIntervalMs = 15000;
  private readonly requestTimeoutMs = 5000;
  private readonly reloadCooldownMs = 5 * 60 * 1000;
  private readonly lastReloadKey = 'leadUiLastAutomaticReload';
  private interval?: ReturnType<typeof setInterval>;
  private checking = false;
  readonly updateAvailable = signal(false);
  private loadedBundle?: string;
  private currentIndexETag?: string;
  private canReload: () => boolean = () => false;

  constructor(private readonly genesysCloudService: GenesysCloudService) { }

  start(canReload: () => boolean): void {
    this.stop();
    this.canReload = canReload;
    this.loadedBundle = this.buildAssets(document);
    if (!this.loadedBundle) return;

    this.interval = setInterval(() => void this.checkNow(), this.checkIntervalMs);
    window.addEventListener('focus', this.onFocus);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    void this.checkNow();
  }

  stop(): void {
    if (this.interval) clearInterval(this.interval);
    this.interval = undefined;
    window.removeEventListener('focus', this.onFocus);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  refreshNow(): void {
    if (this.updateAvailable() && this.canReload()) this.reload(true);
  }

  async checkNow(): Promise<void> {
    if (!this.loadedBundle || this.checking) return;
    // Active calls and disposition work do not need version traffic or a reload.
    if (!this.canReload()) return;
    this.checking = true;
    try {
      if (this.updateAvailable()) {
        await this.refreshHiddenOffQueue();
        return;
      }
      const controller = new AbortController();
      const requestTimeout = setTimeout(() => controller.abort(), this.requestTimeoutMs);
      try {
        const url = new URL('index.html', document.baseURI);
        const headers = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: controller.signal });
        if (!headers.ok) return;
        const etag = headers.headers.get('ETag') ?? undefined;
        if (etag && etag === this.currentIndexETag) return;

        const response = await fetch(url, { cache: 'no-store', signal: controller.signal });
        if (!response.ok) return;
        const deployedBundle = this.buildAssets(new DOMParser().parseFromString(await response.text(), 'text/html'));
        if (!deployedBundle) return;
        this.currentIndexETag = etag;
        if (deployedBundle !== this.loadedBundle) {
          this.updateAvailable.set(true);
          await this.refreshHiddenOffQueue();
        }
      } finally {
        clearTimeout(requestTimeout);
      }
    } catch {
      // Keep the running popup usable if the version check cannot reach the server.
    } finally {
      this.checking = false;
    }
  }

  private readonly onFocus = (): void => { void this.checkNow(); };
  private readonly onVisibilityChange = (): void => { if (document.hidden) void this.checkNow(); };

  private isHidden(): boolean {
    return document.hidden;
  }

  private async refreshHiddenOffQueue(): Promise<void> {
    if (!this.isHidden() || !this.canReload()) return;
    try {
      const user = await firstValueFrom(this.genesysCloudService.getUserMe().pipe(timeout(this.requestTimeoutMs)));
      if (user.routingStatus?.status === 'OFF_QUEUE' && this.isHidden() && this.canReload()) this.reload();
    } catch {
      // If queue state is unavailable, keep the update available for manual refresh.
    }
  }

  private buildAssets(page: Document): string | undefined {
    const paths = Array.from(page.querySelectorAll('script[src], link[rel="stylesheet"][href]'))
      .map(element => element.getAttribute('src') ?? element.getAttribute('href') ?? '')
      .filter(path => /\.[0-9a-f]{8,}\.(?:js|css)(?:\?|$)/i.test(path));
    // The parsed index can include the same stylesheet again inside <noscript>.
    const assets = Array.from(new Set(paths))
      .sort((left, right) => left.localeCompare(right));
    return assets.length ? assets.join('|') : undefined;
  }

  private reserveReload(): boolean {
    // During a rolling deployment different replicas may briefly serve different builds.
    // Limit automatic reloads so a popup cannot bounce between versions.
    try {
      const lastReloadAt = Number(sessionStorage.getItem(this.lastReloadKey));
      if (Date.now() - lastReloadAt < this.reloadCooldownMs) return false;
      sessionStorage.setItem(this.lastReloadKey, String(Date.now()));
    } catch {
      // Without a cross-reload cooldown, leave the visible refresh action in control.
      return false;
    }
    return true;
  }

  private reload(manual = false): void {
    if (!manual && !this.reserveReload()) return;
    // The popup's close guard prompts on every beforeunload, including a safe idle reload.
    const closeGuard = window.onbeforeunload;
    window.onbeforeunload = null;
    try {
      window.location.reload();
    } catch {
      window.onbeforeunload = closeGuard;
    }
  }
}
